import { defineStore } from 'pinia'
import { CardType } from '~/static/enums/card-type'
import { useApi } from '~/utils/FetchApi'
import { convertUrlToFile } from '~/utils/Helpers'
import { EditorObject } from '~/utils/editor/object.class'
import { ObjectType } from '~/static/enums/object-type'
import { ImageObject } from '~/utils/editor/objects/image.class'
import { StepData } from '~/static/enums/editor-state'

interface EditorStore {
  id: number | null
  type: CardType
  isSaved: boolean
  isSavingError: boolean
  steps: StepData[]
  stepBeforeNextAction: StepData | null
  currentStep: number
  savingCount: number
  name: string
  nameForChanging: string
  nameError: string
  generatedImages: string[]
}

interface SavingResponse {
  id: number
}

interface CardResponse {
  card: {
    id: number
    type: CardType
    name: string
    data: string
  }
  generated_urls: string[]
}

export const useEditorStore = defineStore('editor', {
  state: (): EditorStore => {
    return {
      id: null,
      type: CardType.ProductCard,
      isSaved: false,
      isSavingError: false,
      steps: [],
      stepBeforeNextAction: null,
      currentStep: 0,
      savingCount: 1,
      name: 'Без названия',
      nameForChanging: 'Без названия',
      nameError: '',
      generatedImages: [],
    }
  },
  actions: {
    async loadData(id: number) {
      const { response, error } = await useApi<CardResponse>('card.view', { id })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        this.id = response.card.id
        this.name = response.card.name
        this.nameForChanging = response.card.name
        this.type = response.card.type
        this.generatedImages = response.generated_urls
        this.setSteps([ JSON.parse(response.card.data) ], true)
      }
    },
    async save(preview: string) {
      const response = await this.saveCard(preview, this.id)
      if (response) {
        this.id = response.id
        this.isSaved = true
        this.isSavingError = false
      }
    },
    async delete(id?: number) {
      const { response, error } = await useApi<boolean>('card.delete', { id: id ?? this.id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        useRouter().push('/')
      }
    },
    async changeName(): Promise<boolean> {
      if (this.nameForChanging.length === 0) {
        this.nameError = 'Необходимо заполнить поле'
        return false
      } else {
        this.nameError = ''
      }
      if (this.id === null) {
        this.name = this.nameForChanging
        return true
      }
      const { response, error } = await useApi<boolean>('card.saveName', {}, HttpMethod.POST, {
        id: this.id,
        name: this.nameForChanging,
      })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else if (response) {
        this.name = this.nameForChanging
      }
      return response
    },
    async addToTemplates(): Promise<boolean> {
      if (this.id === null || this.type !== CardType.ProductCard) {
        return false
      }
      const { response, error } = await useApi<boolean>('card.moveToTemplate', { id: this.id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else if (response) {
        this.type = CardType.Template
      }
      return response
    },
    async removeFromTemplates(): Promise<boolean> {
      if (this.id === null || this.type !== CardType.Template) {
        return false
      }
      const { response, error } = await useApi<boolean>('card.removeFromTemplate', { id: this.id }, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else if (response) {
        this.type = CardType.ProductCard
      }
      return response
    },
    setSteps(data: StepData[], isSaved: boolean) {
      this.steps = data
      this.isSaved = isSaved
      this.stepBeforeNextAction = null
    },
    addStep(data: StepData) {
      this.isSaved = false
      // Удаление шагов, идущих после текущего (если пользователь откатился назад и снова сделал изменения)
      if (this.currentStep > 0) {
        this.steps.splice(this.steps.length - this.currentStep)
        this.currentStep = 0
      }
      this.steps.push(data)
      // Максимальное число отменяемых шагов - 20, остальные выбрасываются
      if (this.steps.length > 20) {
        this.steps.shift()
      }
    },
    async saveCard(preview: string, id: number | null = null, name: string | null = null) {
      const formData = new FormData()
      if (id !== null) {
        formData.append('id', id.toString())
      }
      if (name !== null) {
        formData.append('name', name)
      } else {
        formData.append('name', this.name)
      }
      formData.append('preview', await convertUrlToFile(preview))
      const stepData = this.getCurrentStep
      formData.append('data', JSON.stringify(stepData))
      const imageUrls: string[] = []
      for (const objectJsonString of stepData.objects) {
        const object: EditorObject = JSON.parse(objectJsonString)
        if (object.type === ObjectType.Image) {
          const image = (object as ImageObject)
          if (image.imageUrl) {
            imageUrls.push(image.imageUrl)
          }
          if (image.imageUrl !== image.originalUrl && image.originalUrl) {
            imageUrls.push(image.originalUrl)
          }
        }
      }
      for (const imageUrl of imageUrls) {
        formData.append('imageUrls[]', imageUrl)
      }
      for (const generatedUrl of this.generatedImages) {
        formData.append('generatedUrls[]', generatedUrl)
      }

      try {
        const { response, error } = await useApi<SavingResponse>('card.save', {}, HttpMethod.POST, formData)

        if (error) {
          // eslint-disable-next-line no-console
          console.log(error.error_code, error.error_msg)
          this.isSavingError = true
        }
        return response
      } catch(e) {
        console.log(e)
        if (id !== null) {
          formData.delete('id')
          const { response, error } = await useApi<SavingResponse>('card.save', {}, HttpMethod.POST, formData)
          if (error) {
            // eslint-disable-next-line no-console
            console.log(error.error_code, error.error_msg)
            this.isSavingError = true
          }
          return response
        }
      }
    },
    async cloneCard(preview: string) {
      const response = await this.saveCard(preview, null, 'Без названия')
      if (response) {
        return response.id
      }
      return null
    },
  },
  getters: {
    getCurrentStep(state): StepData {
      if (state.stepBeforeNextAction !== null) {
        return state.stepBeforeNextAction
      }
      return state.steps[state.steps.length - 1 - state.currentStep]
    },
  },
})
